import React from 'react';
import { Input } from '@/components/ui/input';
import CopyIconButton from '@/components/ui/copy-to-clipboard/copy-icon-button';
import {
  DOCS_URL_OCR,
  ENDPOINT_OCR,
  MODEL_TYPHOON_OCR,
} from '@/features/ai/typhoon-ocr/config';
import { Button } from '@/components/ui/button';
import { BookText, ExternalLink } from 'lucide-react';
import { SyntaxHighlighter } from '@/components/ui/syntax-highlighter';
import { DocLinkCardHorizontal } from '@/pages/dashboard/serverless-gpu/detail/overview/doc';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { getTokenOption } from '@/service/ai/hook';
import { useQuery } from '@tanstack/react-query';
import useParamWorkspaceId from '@/hook/useParamWorkspaceId';

const AiServiceOCROverview = () => {
  const { workspaceId } = useParamWorkspaceId();
  const { data: token } = useQuery(
    getTokenOption({
      workspace_id: workspaceId,
    })
  );

  const curlExample = `curl -X POST ${ENDPOINT_OCR}/chat/completions \\
  --header 'Content-Type: application/json' \\
  --header 'Authorization: Bearer ${token?.saas_token}' \\
  --data '{
    "model": "${MODEL_TYPHOON_OCR}",
    "messages": [
      {
        "role": "user",
        "content": {
          "type": "image_url",
          "image_url": {
            "url": ""
          }
        }
      }
    ]
  }'`;

  const pythonExample = `from typhoon_ocr import ocr_document

# Process PDF document
markdown = ocr_document(
    pdf_or_image_path="document.pdf",  # Works with PDFs or images
    task_type="default",               # Choose between "default" or "structure"
    page_num=2,                        # Process page 2 of a PDF (default is 1, always 1 for images)
    #Config Base URL and API Key
    base_url="${ENDPOINT_OCR}",
    api_key="${token?.saas_token}"
)

# Process image
markdown = ocr_document(
    pdf_or_image_path="scan.jpg",      # Works with PDFs or images
    task_type="default",               # Choose between "default" or "structure"
    #Config Base URL and API Key
    base_url="${ENDPOINT_OCR}",
    api_key="${token?.saas_token}"
)

print(markdown)`;

  return (
    <div className="container mx-auto pt-10">
      {/* Title & Description */}
      <h1 className="mb-2 text-2xl font-bold">Typhoon OCR</h1>
      <p className="mb-6 text-muted-foreground">
        OpenTyphoon.ai offers specialized OCR model optimized for Thai text
        recognition and document processing. Our OCR model is designed to handle
        various types of documents, images, and forms with high accuracy.
      </p>
      <div className="space-y-8">
        {/* Endpoint Section */}
        <div>
          <h2 className="mb-2 text-base font-semibold">Endpoint</h2>
          <div className="flex max-w-[480px] items-center gap-2">
            <Input
              type="text"
              value={`${ENDPOINT_OCR}/chat/completions`}
              readOnly
              className="font-mono text-sm"
              endIcon={<CopyIconButton text={`${ENDPOINT_OCR}/chat/completions` || ''} />}
            />
          </div>
        </div>
        <Separator />

        {/* Example Request Section with Tabs */}
        <div>
          <h2 className="mb-4 text-base font-semibold">Example Request</h2>
          <Tabs defaultValue="python" className="w-full">
            <TabsList variant="outline">
              <TabsTrigger value="python" variant="outline">
                Python
              </TabsTrigger>
              <TabsTrigger value="curl" variant="outline">
                cURL
              </TabsTrigger>
            </TabsList>

            <TabsContent value="python" className="mt-4">
              <SyntaxHighlighter language="python" showCopy wrapLongLines>
                {pythonExample}
              </SyntaxHighlighter>
            </TabsContent>
            <TabsContent value="curl" className="mt-4">
              <SyntaxHighlighter language="bash" showCopy wrapLongLines>
                {curlExample}
              </SyntaxHighlighter>
            </TabsContent>
          </Tabs>
        </div>

        {/* Go to Docs Section */}
        <div className="space-y-4">
          <div className="space-y-1.5">
            <div className="text-base font-semibold">Documentation</div>
            <p className="text-sm text-muted-foreground">
              Explore our comprehensive documentation to get the most out of
              this service.
            </p>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            <DocLinkCardHorizontal
              title="Typhoon API Docs"
              description="See full API reference and updates."
              url={DOCS_URL_OCR}
              icon={BookText}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AiServiceOCROverview;
